<script setup lang="ts">
import { isJust, type Maybe } from "../lib/maybe";

/**
 * Shared flyout shell above the editor status bar (history, goals, etc.).
 * Hosted by EditorStatusBar; feature content teleports into the content slot.
 */
defineProps<{
  open: boolean;
  title: string;
  /** Optional muted line under the title. */
  subtitle: Maybe<string>;
  /** Accessible name when title alone is not enough. */
  ariaLabel?: string;
}>();

const emit = defineEmits<{
  close: [];
}>();
</script>

<template>
  <aside
    class="zw-dock-panel"
    :class="{ 'zw-dock-panel-open': open }"
    :aria-hidden="!open"
    :inert="!open"
    :aria-label="
      typeof ariaLabel === 'string' && ariaLabel.length > 0 ? ariaLabel : title
    "
  >
    <div class="zw-dock-panel-clip">
      <div class="zw-dock-panel-body">
        <header
          class="flex shrink-0 items-start justify-between gap-2 border-b border-(--line) px-3 py-2.5"
        >
          <div class="min-w-0">
            <h2
              class="font-heading text-[0.95rem] font-medium tracking-tight text-(--text-strong)"
            >
              {{ title }}
            </h2>
            <p
              v-if="isJust(subtitle)"
              class="mt-0.5 truncate font-ui text-[0.72rem] text-(--text-muted)"
            >
              {{ subtitle.value }}
            </p>
          </div>
          <button
            type="button"
            class="shrink-0 rounded-[0.3rem] p-1.5 text-(--text-muted) transition-colors hover:bg-(--hover-fill) hover:text-(--text-strong)"
            :title="`Close ${title.toLowerCase()}`"
            :aria-label="`Close ${title.toLowerCase()}`"
            @click="emit('close')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              class="size-4"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </header>
        <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
          <slot />
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.zw-dock-panel {
  display: grid;
  grid-template-rows: 0fr;
  width: 15.5rem;
  pointer-events: none;
  transition: grid-template-rows var(--duration-med) var(--ease-out-quart);
}

.zw-dock-panel-open {
  grid-template-rows: 1fr;
  pointer-events: auto;
}

.zw-dock-panel-clip {
  min-height: 0;
  overflow: hidden;
}

.zw-dock-panel-body {
  display: flex;
  max-height: min(28rem, calc(100vh - 3.5rem));
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--line);
  border-right: none;
  border-bottom: none;
  border-radius: 0.35rem 0 0 0;
  background: var(--bg-raised);
}
</style>

<style>
html:is([data-zw-mobile-shell="1"], [data-zw-narrow="1"]) .zw-dock-panel {
  width: min(100vw - 1rem, 15.5rem);
}

html:is([data-zw-mobile-shell="1"], [data-zw-narrow="1"]) .zw-dock-panel-body {
  /* svh tracks the layout viewport, which Android doesn't shrink for the IME —
     subtract the keyboard explicitly. */
  max-height: min(40svh, calc(100svh - var(--zw-keyboard-inset, 0px) - 5.5rem));
}
</style>
